import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams, Link } from 'react-router-dom';
import {
  MdArrowBack, MdEdit, MdPersonAdd, MdSave, MdClose, MdCheckCircle, MdCancel, MdWarning,
  MdPhone, MdEmail, MdEvent, MdChat, MdNote, MdAdd, MdDelete, MdHistory, MdLabel,
  MdSchedule, MdDescription, MdMoreHoriz,
} from 'react-icons/md';
import api, { usuariosAPI, leadsAPI } from '../services/api';
import { useModal } from '../context/ModalContext';
import Adjuntos from '../components/Adjuntos';
import CalendarioRecordatorios from '../components/CalendarioRecordatorios';

// ─── Styled components ────────────────────────────────────────────────────────

const Container = styled.div`
  width: 100%;
  max-width: 100%;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.lg};
  margin-bottom: ${p => p.theme.spacing.xl};
  flex-wrap: wrap;
`;

const BackButton = styled.button`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${p => p.theme.colors.backgroundLight};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  color: ${p => p.theme.colors.textPrimary};
  cursor: pointer;
  transition: ${p => p.theme.transitions.fast};
  svg { font-size: 1.5rem; }
  &:hover { border-color: ${p => p.theme.colors.primary}; color: ${p => p.theme.colors.primary}; }
`;

const HeaderInfo = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.md};
  flex-wrap: wrap;
`;

const Title = styled.h1`
  font-size: ${p => p.theme.fontSizes.xxxl};
  color: ${p => p.theme.colors.textPrimary};
  margin: 0;
`;

const StatusBadge = styled.span`
  padding: ${p => p.theme.spacing.xs} ${p => p.theme.spacing.md};
  border-radius: ${p => p.theme.borderRadius.sm};
  font-size: ${p => p.theme.fontSizes.sm};
  font-weight: ${p => p.theme.fontWeights.medium};
  background-color: ${p => {
    const s = p.status?.toLowerCase();
    if (s === 'nuevo')      return p.theme.colors.primary + '20';
    if (s === 'contactado') return p.theme.colors.warning + '20';
    if (s === 'calificado') return p.theme.colors.success + '20';
    if (s === 'ganado')     return p.theme.colors.success + '30';
    if (s === 'perdido')    return p.theme.colors.danger  + '20';
    if (s === 'descartado') return p.theme.colors.textSecondary + '20';
    return p.theme.colors.primary + '20';
  }};
  color: ${p => {
    const s = p.status?.toLowerCase();
    if (s === 'nuevo')      return p.theme.colors.primary;
    if (s === 'contactado') return p.theme.colors.warning;
    if (s === 'calificado') return p.theme.colors.success;
    if (s === 'ganado')     return p.theme.colors.success;
    if (s === 'perdido')    return p.theme.colors.danger;
    if (s === 'descartado') return p.theme.colors.textSecondary;
    return p.theme.colors.primary;
  }};
`;

const HeaderActions = styled.div`
  display: flex;
  gap: ${p => p.theme.spacing.md};
  flex-wrap: wrap;
`;

const Btn = styled.button`
  padding: ${p => p.theme.spacing.sm} ${p => p.theme.spacing.lg};
  background-color: ${p =>
    p.variant === 'success' ? p.theme.colors.success :
    p.variant === 'danger'  ? p.theme.colors.danger  :
    p.primary ? p.theme.colors.primary :
    p.theme.colors.backgroundLight};
  color: ${p => (p.primary || p.variant) ? 'white' : p.theme.colors.textPrimary};
  border: 1px solid ${p =>
    p.variant === 'success' ? p.theme.colors.success :
    p.variant === 'danger'  ? p.theme.colors.danger  :
    p.primary ? p.theme.colors.primary :
    p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.sm};
  font-weight: ${p => p.theme.fontWeights.semibold};
  display: inline-flex;
  align-items: center;
  gap: ${p => p.theme.spacing.sm};
  cursor: pointer;
  transition: ${p => p.theme.transitions.fast};
  svg { font-size: 1.2rem; }
  &:hover { transform: translateY(-1px); box-shadow: ${p => p.theme.shadows.sm}; opacity: 0.9; }
  &:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }
`;

// Tags row
const TagsRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${p => p.theme.spacing.sm};
  margin-bottom: ${p => p.theme.spacing.lg};
  padding: ${p => p.theme.spacing.md} ${p => p.theme.spacing.lg};
  background: ${p => p.theme.colors.backgroundLight};
  border-radius: ${p => p.theme.borderRadius.md};
  border: 1px solid ${p => p.theme.colors.border};
`;

const TagsLabel = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: ${p => p.theme.fontSizes.xs};
  color: ${p => p.theme.colors.textLight};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 700;
  margin-right: ${p => p.theme.spacing.xs};
  svg { font-size: 1rem; }
`;

const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  background: ${p => p.theme.colors.primary}18;
  color: ${p => p.theme.colors.primary};
  border-radius: 999px;
  font-size: ${p => p.theme.fontSizes.xs};
  font-weight: ${p => p.theme.fontWeights.medium};
`;

const TagRemoveBtn = styled.button`
  display: flex;
  align-items: center;
  background: none;
  border: none;
  color: ${p => p.theme.colors.primary};
  cursor: pointer;
  padding: 0;
  margin-left: 2px;
  opacity: 0.6;
  font-size: 0.85rem;
  &:hover { opacity: 1; }
`;

const TagInput = styled.input`
  padding: 3px 8px;
  border: 1px dashed ${p => p.theme.colors.border};
  border-radius: 999px;
  font-size: ${p => p.theme.fontSizes.xs};
  background: transparent;
  color: ${p => p.theme.colors.textPrimary};
  width: 110px;
  &:focus { outline: none; border-color: ${p => p.theme.colors.primary}; }
  &::placeholder { color: ${p => p.theme.colors.textLight}; }
`;

// Content grid
const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${p => p.theme.spacing.xl};
  margin-bottom: ${p => p.theme.spacing.xl};
  @media (max-width: ${p => p.theme.breakpoints.desktop}) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: ${p => p.theme.colors.backgroundLight};
  border-radius: ${p => p.theme.borderRadius.lg};
  padding: ${p => p.theme.spacing.xl};
  box-shadow: ${p => p.theme.shadows.card};
`;

const CardTitle = styled.h2`
  font-size: ${p => p.theme.fontSizes.xl};
  color: ${p => p.theme.colors.textPrimary};
  margin: 0 0 ${p => p.theme.spacing.lg} 0;
  font-weight: ${p => p.theme.fontWeights.semibold};
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.sm};
  svg { font-size: 1.2rem; color: ${p => p.theme.colors.primary}; }
`;

const InfoRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${p => p.theme.spacing.xs};
  margin-bottom: ${p => p.theme.spacing.md};
`;

const InfoLabel = styled.span`
  font-size: ${p => p.theme.fontSizes.xs};
  color: ${p => p.theme.colors.textLight};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 700;
`;

const InfoValue = styled.span`
  font-size: ${p => p.theme.fontSizes.sm};
  color: ${p => p.theme.colors.textPrimary};
`;

const Input = styled.input`
  padding: ${p => p.theme.spacing.md};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.sm};
  background-color: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.textPrimary};
  transition: ${p => p.theme.transitions.fast};
  &:focus { border-color: ${p => p.theme.colors.primary}; outline: none; }
`;

const Select = styled.select`
  padding: ${p => p.theme.spacing.md};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.sm};
  background-color: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.textPrimary};
  cursor: pointer;
  &:focus { border-color: ${p => p.theme.colors.primary}; outline: none; }
`;

const TextArea = styled.textarea`
  padding: ${p => p.theme.spacing.md};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.sm};
  background-color: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.textPrimary};
  font-family: ${p => p.theme.fonts.primary};
  min-height: 90px;
  resize: vertical;
  &:focus { border-color: ${p => p.theme.colors.primary}; outline: none; }
`;

const EditButtons = styled.div`
  display: flex;
  gap: ${p => p.theme.spacing.md};
  justify-content: flex-end;
  margin-top: ${p => p.theme.spacing.lg};
`;

const ConvertedBadge = styled.span`
  padding: ${p => p.theme.spacing.xs} ${p => p.theme.spacing.md};
  background: ${p => p.theme.colors.success + '20'};
  color: ${p => p.theme.colors.success};
  border-radius: ${p => p.theme.borderRadius.sm};
  font-size: ${p => p.theme.fontSizes.sm};
  font-weight: ${p => p.theme.fontWeights.semibold};
`;

const ErrorMsg = styled.p`
  color: ${p => p.theme.colors.danger};
  font-size: ${p => p.theme.fontSizes.sm};
  margin: ${p => p.theme.spacing.md} 0 0;
  padding: ${p => p.theme.spacing.md};
  background: ${p => p.theme.colors.danger}10;
  border-radius: ${p => p.theme.borderRadius.md};
  border-left: 3px solid ${p => p.theme.colors.danger};
`;

const StaleAlert = styled.div`
  background: ${p => p.theme.colors.warning}10;
  border: 1px solid ${p => p.theme.colors.warning}40;
  border-left: 4px solid ${p => p.theme.colors.warning};
  border-radius: ${p => p.theme.borderRadius.md};
  padding: ${p => p.theme.spacing.md} ${p => p.theme.spacing.lg};
  margin-bottom: ${p => p.theme.spacing.lg};
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.md};
  font-size: ${p => p.theme.fontSizes.sm};
  color: ${p => p.theme.colors.textPrimary};
  svg { color: ${p => p.theme.colors.warning}; font-size: 1.2rem; flex-shrink: 0; }
`;

const CloseErrAlert = styled.div`
  background: ${p => p.theme.colors.danger}10;
  border: 1px solid ${p => p.theme.colors.danger}40;
  border-left: 4px solid ${p => p.theme.colors.danger};
  border-radius: ${p => p.theme.borderRadius.md};
  padding: ${p => p.theme.spacing.md} ${p => p.theme.spacing.lg};
  margin-bottom: ${p => p.theme.spacing.lg};
  font-size: ${p => p.theme.fontSizes.sm};
  color: ${p => p.theme.colors.danger};
`;

// Próxima acción inline badge
const ProximaBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  background: ${p => p.theme.colors.warning}18;
  color: ${p => p.theme.colors.warning};
  border-radius: ${p => p.theme.borderRadius.sm};
  font-size: ${p => p.theme.fontSizes.xs};
  font-weight: ${p => p.theme.fontWeights.medium};
  svg { font-size: 0.95rem; }
`;

// Timeline section
const SectionCard = styled.div`
  background: ${p => p.theme.colors.backgroundLight};
  border-radius: ${p => p.theme.borderRadius.lg};
  padding: ${p => p.theme.spacing.xl};
  box-shadow: ${p => p.theme.shadows.card};
  margin-bottom: ${p => p.theme.spacing.xl};
`;

const AddActivityForm = styled.div`
  display: flex;
  gap: ${p => p.theme.spacing.md};
  margin-bottom: ${p => p.theme.spacing.lg};
  align-items: flex-start;
  flex-wrap: wrap;
`;

const ActivityList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ActivityItem = styled.div`
  display: flex;
  gap: ${p => p.theme.spacing.md};
  padding: ${p => p.theme.spacing.md} ${p => p.theme.spacing.sm};
  border-radius: ${p => p.theme.borderRadius.md};
  align-items: flex-start;
  &:hover { background: ${p => p.theme.colors.background}; }
`;

const ActivityIconWrap = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: ${p => {
    if (p.tipo === 'llamada')  return p.theme.colors.success  + '20';
    if (p.tipo === 'email')    return p.theme.colors.info     + '20';
    if (p.tipo === 'reunion')  return p.theme.colors.warning  + '20';
    if (p.tipo === 'whatsapp') return '#25D36620';
    return p.theme.colors.primary + '20';
  }};
  color: ${p => {
    if (p.tipo === 'llamada')  return p.theme.colors.success;
    if (p.tipo === 'email')    return p.theme.colors.info;
    if (p.tipo === 'reunion')  return p.theme.colors.warning;
    if (p.tipo === 'whatsapp') return '#25D366';
    return p.theme.colors.primary;
  }};
  svg { font-size: 1rem; }
`;

const ActivityBody = styled.div`
  flex: 1;
`;

const ActivityDesc = styled.p`
  margin: 0 0 4px 0;
  font-size: ${p => p.theme.fontSizes.sm};
  color: ${p => p.theme.colors.textPrimary};
  white-space: pre-wrap;
`;

const ActivityMeta = styled.span`
  font-size: ${p => p.theme.fontSizes.xs};
  color: ${p => p.theme.colors.textLight};
`;

const ActivityDeleteBtn = styled.button`
  background: none;
  border: none;
  color: ${p => p.theme.colors.textLight};
  cursor: pointer;
  padding: 4px;
  border-radius: ${p => p.theme.borderRadius.sm};
  display: flex;
  align-items: center;
  opacity: 0;
  transition: opacity 0.15s;
  ${ActivityItem}:hover & { opacity: 1; }
  &:hover { color: ${p => p.theme.colors.danger}; background: ${p => p.theme.colors.danger}10; }
  svg { font-size: 1rem; }
`;

const EmptyMsg = styled.p`
  text-align: center;
  color: ${p => p.theme.colors.textLight};
  font-size: ${p => p.theme.fontSizes.sm};
  padding: ${p => p.theme.spacing.xl} 0;
  margin: 0;
`;

// Historial de estados
const HistorialList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const HistorialItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${p => p.theme.spacing.md};
  padding: ${p => p.theme.spacing.sm} 0;
  position: relative;
  &:not(:last-child)::before {
    content: '';
    position: absolute;
    left: 11px;
    top: 28px;
    bottom: -8px;
    width: 2px;
    background: ${p => p.theme.colors.border};
  }
`;

const HistorialDot = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.6rem;
  font-weight: 700;
  color: white;
  background: ${p => {
    const s = p.estado;
    if (s === 'nuevo')      return p.theme.colors.primary;
    if (s === 'contactado') return p.theme.colors.warning;
    if (s === 'calificado') return '#8b5cf6';
    if (s === 'ganado')     return p.theme.colors.success;
    if (s === 'perdido')    return p.theme.colors.danger;
    return p.theme.colors.textSecondary;
  }};
`;

const HistorialText = styled.div`
  flex: 1;
  font-size: ${p => p.theme.fontSizes.xs};
  color: ${p => p.theme.colors.textSecondary};
  padding-top: 4px;
  strong { color: ${p => p.theme.colors.textPrimary}; font-weight: ${p => p.theme.fontWeights.semibold}; }
`;

// ─── Helpers ─────────────────────────────────────────────────────────────────

const TIPO_OPTIONS = [
  { value: 'nota',     label: 'Nota'     },
  { value: 'llamada',  label: 'Llamada'  },
  { value: 'email',    label: 'Email'    },
  { value: 'reunion',  label: 'Reunión'  },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'otro',     label: 'Otro'     },
];

const ACCION_TIPOS = ['llamada','email','demo','propuesta','reunion','otro'];

function actIcon(tipo) {
  if (tipo === 'llamada')  return <MdPhone />;
  if (tipo === 'email')    return <MdEmail />;
  if (tipo === 'reunion')  return <MdEvent />;
  if (tipo === 'whatsapp') return <MdChat />;
  return <MdNote />;
}

function fmtDatetime(dt) {
  if (!dt) return '';
  return new Date(dt).toLocaleString('es-MX', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function fmtDate(dt) {
  if (!dt) return '';
  return new Date(dt).toLocaleDateString('es-MX');
}

// ─── Component ───────────────────────────────────────────────────────────────

const LeadDetail = () => {
  const navigate   = useNavigate();
  const { id }     = useParams();
  const { confirm, toast } = useModal();

  // Lead state
  const [lead, setLead]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  // Edit
  const [editing, setEditing]   = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving]     = useState(false);
  const [editError, setEditError] = useState(null);

  // Actions
  const [converting, setConverting]   = useState(false);
  const [usuarios, setUsuarios]       = useState([]);
  const [reassigning, setReassigning] = useState(false);
  const [closing, setClosing]         = useState(false);
  const [closeError, setCloseError]   = useState(null);

  // Tags
  const [etiquetas, setEtiquetas] = useState([]);
  const [newTag, setNewTag]       = useState('');
  const tagInputRef = useRef(null);

  // Actividades
  const [actividades, setActividades] = useState([]);
  const [actLoading, setActLoading]   = useState(false);
  const [newAct, setNewAct]           = useState({ tipo: 'nota', descripcion: '' });
  const [addingAct, setAddingAct]     = useState(false);

  // Historial de estados
  const [historial, setHistorial] = useState([]);

  useEffect(() => {
    loadLead();
    loadActividades();
    loadHistorial();
    usuariosAPI.getAll().then(r => setUsuarios(r.data.data || [])).catch(() => {});
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadLead = async () => {
    try {
      setLoading(true);
      const res  = await api.get(`/leads/${id}`);
      const data = res.data.data || res.data;
      setLead(data);
      try { setEtiquetas(data.etiquetas ? JSON.parse(data.etiquetas) : []); } catch { setEtiquetas([]); }
      setEditForm({
        nombre:               data.nombre               || '',
        empresa:              data.empresa              || '',
        email:                data.email                || '',
        telefono:             data.telefono             || '',
        origen:               data.origen               || 'web',
        estado:               data.estado               || 'nuevo',
        fecha_primer_contacto: data.fecha_primer_contacto
          ? data.fecha_primer_contacto.split('T')[0] : '',
        interes_inicial:      data.interes_inicial      || '',
        notas:                data.notas                || '',
        proxima_accion_tipo:  data.proxima_accion_tipo  || '',
        proxima_accion_fecha: data.proxima_accion_fecha
          ? data.proxima_accion_fecha.split('T')[0] : '',
      });
      setError(null);
    } catch (err) {
      setError(err.message || 'Error al cargar lead');
    } finally {
      setLoading(false);
    }
  };

  const loadActividades = async () => {
    try {
      setActLoading(true);
      const res = await leadsAPI.getActividades(id);
      setActividades(res.data.data || []);
    } catch { setActividades([]); } finally { setActLoading(false); }
  };

  const loadHistorial = async () => {
    try {
      const res = await leadsAPI.getHistorial(id);
      setHistorial(res.data.data || []);
    } catch { setHistorial([]); }
  };

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleEditChange = e => {
    const { name, value } = e.target;
    setEditForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveEdit = async e => {
    e.preventDefault();
    setSaving(true);
    setEditError(null);
    try {
      await api.put(`/leads/${id}`, editForm);
      setEditing(false);
      await loadLead();
      await loadHistorial();
    } catch (err) {
      setEditError(err.message || 'Error al guardar cambios');
    } finally {
      setSaving(false);
    }
  };

  const handleConvertir = async () => {
    const ok = await confirm('¿Convertir este lead a cliente? Se creará un nuevo cliente con los datos del lead.', {
      title: 'Convertir a Cliente',
      confirmText: 'Convertir',
      cancelText: 'Cancelar',
    });
    if (!ok) return;
    setConverting(true);
    try {
      const res = await api.post(`/leads/${id}/convertir`);
      const clienteId = res.data.data?.cliente_id || res.data.cliente_id;
      navigate(`/clientes/${clienteId}`);
    } catch (err) {
      toast('Error al convertir: ' + (err.message || 'Error desconocido'), 'error');
    } finally {
      setConverting(false);
    }
  };

  const handleReasignar = async nuevoUsuarioId => {
    setReassigning(true);
    try {
      await api.put(`/leads/${id}`, { asignado_a: nuevoUsuarioId || null });
      const res = await api.get(`/leads/${id}`);
      setLead(res.data.data || res.data);
      toast('Responsable actualizado', 'success');
    } catch (err) {
      toast('Error al reasignar: ' + (err.message || 'Error desconocido'), 'error');
    } finally {
      setReassigning(false);
    }
  };

  const handleCerrar = async nuevoEstado => {
    setClosing(true);
    setCloseError(null);
    try {
      await api.put(`/leads/${id}`, { estado: nuevoEstado });
      await loadLead();
      await loadHistorial();
      toast(`Lead marcado como ${nuevoEstado}`, 'success');
    } catch (err) {
      if (err.stale) {
        setCloseError(err.message || `Este lead lleva ${err.dias_sin_actividad} días sin actividad.`);
      } else {
        toast('Error: ' + (err.message || 'Error desconocido'), 'error');
      }
    } finally {
      setClosing(false);
    }
  };

  // Tags
  const handleAddTag = async () => {
    const tag = newTag.trim().toLowerCase().replace(/\s+/g, '-');
    if (!tag || etiquetas.includes(tag)) { setNewTag(''); return; }
    const next = [...etiquetas, tag];
    setEtiquetas(next);
    setNewTag('');
    try {
      await api.put(`/leads/${id}`, { etiquetas: JSON.stringify(next) });
    } catch {
      setEtiquetas(etiquetas);
      toast('Error al guardar etiqueta', 'error');
    }
  };

  const handleRemoveTag = async tag => {
    const next = etiquetas.filter(t => t !== tag);
    setEtiquetas(next);
    try {
      await api.put(`/leads/${id}`, { etiquetas: JSON.stringify(next) });
    } catch {
      setEtiquetas(etiquetas);
      toast('Error al eliminar etiqueta', 'error');
    }
  };

  // Actividades
  const handleAddActividad = async e => {
    e.preventDefault();
    if (!newAct.descripcion.trim()) return;
    setAddingAct(true);
    try {
      await leadsAPI.createActividad(id, newAct);
      setNewAct({ tipo: 'nota', descripcion: '' });
      await loadActividades();
      await loadLead(); // refresh updated_at for stale check
    } catch {
      toast('Error al agregar actividad', 'error');
    } finally {
      setAddingAct(false);
    }
  };

  const handleDeleteActividad = async actId => {
    try {
      await leadsAPI.deleteActividad(actId);
      setActividades(prev => prev.filter(a => a.id !== actId));
    } catch {
      toast('Error al eliminar actividad', 'error');
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Container>
        <div style={{ textAlign: 'center', padding: '60px' }}>
          <p>Cargando lead...</p>
        </div>
      </Container>
    );
  }

  if (error || !lead) {
    return (
      <Container>
        <div style={{ textAlign: 'center', padding: '60px', color: '#ef4444' }}>
          <p>Error: {error || 'Lead no encontrado'}</p>
          <Btn onClick={() => navigate('/leads')}>Volver a Leads</Btn>
        </div>
      </Container>
    );
  }

  const diasSinActividad = lead?.updated_at
    ? Math.floor((Date.now() - new Date(lead.updated_at).getTime()) / 86400000)
    : 0;
  const isClosed = ['ganado', 'perdido', 'descartado'].includes(lead?.estado?.toLowerCase());

  return (
    <Container>
      {/* ── Header ── */}
      <PageHeader>
        <BackButton onClick={() => navigate(-1)}><MdArrowBack /></BackButton>
        <HeaderInfo>
          <Title>{lead.nombre}</Title>
          <StatusBadge status={lead.estado}>
            {lead.estado ? lead.estado.charAt(0).toUpperCase() + lead.estado.slice(1) : 'N/A'}
          </StatusBadge>
          {lead.convertido_cliente_id && <ConvertedBadge>Ya convertido</ConvertedBadge>}
        </HeaderInfo>
        <HeaderActions>
          <Btn onClick={() => setEditing(e => !e)}>
            {editing ? <MdClose /> : <MdEdit />}
            {editing ? 'Cancelar edición' : 'Editar'}
          </Btn>
          {!isClosed && (
            <>
              <Btn variant="success" onClick={() => handleCerrar('ganado')} disabled={closing}>
                <MdCheckCircle /> Ganado
              </Btn>
              <Btn variant="danger" onClick={() => handleCerrar('perdido')} disabled={closing}>
                <MdCancel /> Perdido
              </Btn>
            </>
          )}
          {!lead.convertido_cliente_id ? (
            <Btn onClick={handleConvertir} disabled={converting} style={{ background: 'transparent' }}>
              <MdPersonAdd />
              {converting ? 'Convirtiendo...' : 'Convertir a Cliente'}
            </Btn>
          ) : (
            <Link to={`/clientes/${lead.convertido_cliente_id}`} style={{ textDecoration: 'none' }}>
              <Btn primary>Ver Cliente</Btn>
            </Link>
          )}
          <Btn onClick={() => navigate('/cotizaciones', { state: { fromLead: { id: lead.id, nombre: lead.nombre, empresa: lead.empresa, email: lead.email } } })}>
            <MdDescription /> Nueva Cotización
          </Btn>
        </HeaderActions>
      </PageHeader>

      {/* ── Tags ── */}
      <TagsRow>
        <TagsLabel><MdLabel /> Etiquetas</TagsLabel>
        {etiquetas.map(tag => (
          <Tag key={tag}>
            {tag}
            <TagRemoveBtn onClick={() => handleRemoveTag(tag)} title="Eliminar">×</TagRemoveBtn>
          </Tag>
        ))}
        <TagInput
          ref={tagInputRef}
          value={newTag}
          placeholder="+ nueva etiqueta"
          onChange={e => setNewTag(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddTag(); } }}
          onBlur={handleAddTag}
        />
      </TagsRow>

      {/* ── Alerts ── */}
      {!isClosed && diasSinActividad >= 7 && (
        <StaleAlert>
          <MdWarning />
          <span>
            <strong>{diasSinActividad} días sin actividad.</strong> Actualiza las notas o información del lead para poder cerrarlo como ganado o perdido.
          </span>
        </StaleAlert>
      )}
      {closeError && (
        <CloseErrAlert>
          <strong>No se puede cerrar el lead:</strong> {closeError}
        </CloseErrAlert>
      )}

      {/* ── Info cards ── */}
      <form onSubmit={handleSaveEdit}>
        <ContentGrid>
          <Card>
            <CardTitle>Información Básica</CardTitle>
            <InfoRow>
              <InfoLabel>Nombre</InfoLabel>
              <InfoValue>
                {editing
                  ? <Input name="nombre" value={editForm.nombre} onChange={handleEditChange} required style={{ width: '100%' }} />
                  : lead.nombre || 'N/A'}
              </InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Empresa</InfoLabel>
              <InfoValue>
                {editing
                  ? <Input name="empresa" value={editForm.empresa} onChange={handleEditChange} style={{ width: '100%' }} />
                  : lead.empresa || 'N/A'}
              </InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Email</InfoLabel>
              <InfoValue>
                {editing
                  ? <Input type="email" name="email" value={editForm.email} onChange={handleEditChange} style={{ width: '100%' }} />
                  : lead.email || 'N/A'}
              </InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Teléfono</InfoLabel>
              <InfoValue>
                {editing
                  ? <Input name="telefono" value={editForm.telefono} onChange={handleEditChange} style={{ width: '100%' }} />
                  : lead.telefono || 'N/A'}
              </InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Origen</InfoLabel>
              <InfoValue>
                {editing
                  ? <Select name="origen" value={editForm.origen} onChange={handleEditChange} style={{ width: '100%' }}>
                      <option value="web">Web</option>
                      <option value="referido">Referido</option>
                      <option value="llamada">Llamada</option>
                      <option value="expo">Expo</option>
                      <option value="redes_sociales">Redes Sociales</option>
                      <option value="otro">Otro</option>
                    </Select>
                  : lead.origen || 'N/A'}
              </InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Estado</InfoLabel>
              <InfoValue>
                {editing
                  ? <Select name="estado" value={editForm.estado} onChange={handleEditChange} style={{ width: '100%' }}>
                      <option value="nuevo">Nuevo</option>
                      <option value="contactado">Contactado</option>
                      <option value="calificado">Calificado</option>
                      <option value="descartado">Descartado</option>
                    </Select>
                  : <StatusBadge status={lead.estado}>
                      {lead.estado ? lead.estado.charAt(0).toUpperCase() + lead.estado.slice(1) : 'N/A'}
                    </StatusBadge>}
              </InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Primer Contacto</InfoLabel>
              <InfoValue>
                {editing
                  ? <Input type="date" name="fecha_primer_contacto" value={editForm.fecha_primer_contacto} onChange={handleEditChange} style={{ width: '100%' }} />
                  : lead.fecha_primer_contacto ? fmtDate(lead.fecha_primer_contacto) : 'N/A'}
              </InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Registrado</InfoLabel>
              <InfoValue>{lead.created_at ? fmtDate(lead.created_at) : 'N/A'}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Responsable</InfoLabel>
              <InfoValue>
                <select
                  value={lead.asignado_a || ''}
                  onChange={e => handleReasignar(e.target.value)}
                  disabled={reassigning}
                  style={{ padding: '4px 8px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '0.875rem', background: 'transparent', cursor: 'pointer', fontFamily: 'inherit' }}
                >
                  <option value="">Sin asignar</option>
                  {usuarios.map(u => (
                    <option key={u.id} value={u.id}>{u.nombre}</option>
                  ))}
                </select>
              </InfoValue>
            </InfoRow>

            {/* Próxima acción */}
            <InfoRow style={{ marginTop: 8 }}>
              <InfoLabel><MdSchedule style={{ verticalAlign: 'middle', marginRight: 2 }} />Próxima Acción</InfoLabel>
              <InfoValue>
                {editing ? (
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <Select name="proxima_accion_tipo" value={editForm.proxima_accion_tipo} onChange={handleEditChange} style={{ flex: '1 1 120px' }}>
                      <option value="">Sin acción</option>
                      {ACCION_TIPOS.map(t => (
                        <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                      ))}
                    </Select>
                    <Input type="date" name="proxima_accion_fecha" value={editForm.proxima_accion_fecha} onChange={handleEditChange} style={{ flex: '1 1 140px' }} />
                  </div>
                ) : lead.proxima_accion_tipo ? (
                  <ProximaBadge>
                    <MdSchedule />
                    {lead.proxima_accion_tipo.charAt(0).toUpperCase() + lead.proxima_accion_tipo.slice(1)}
                    {lead.proxima_accion_fecha ? ` · ${fmtDate(lead.proxima_accion_fecha)}` : ''}
                  </ProximaBadge>
                ) : (
                  <span style={{ color: 'var(--text-light, #9ca3af)', fontStyle: 'italic' }}>Sin próxima acción</span>
                )}
              </InfoValue>
            </InfoRow>
          </Card>

          <Card>
            <CardTitle>Notas e Interés</CardTitle>
            <InfoRow>
              <InfoLabel>Interés Inicial</InfoLabel>
              <InfoValue style={{ whiteSpace: 'pre-wrap' }}>
                {editing
                  ? <TextArea name="interes_inicial" value={editForm.interes_inicial} onChange={handleEditChange} style={{ width: '100%' }} />
                  : lead.interes_inicial || 'Sin información de interés'}
              </InfoValue>
            </InfoRow>
            <InfoRow style={{ marginTop: '16px' }}>
              <InfoLabel>Notas</InfoLabel>
              <InfoValue style={{ whiteSpace: 'pre-wrap' }}>
                {editing
                  ? <TextArea name="notas" value={editForm.notas} onChange={handleEditChange} style={{ width: '100%' }} />
                  : lead.notas || 'Sin notas'}
              </InfoValue>
            </InfoRow>
            {editing && (
              <EditButtons style={{ marginTop: '16px' }}>
                {editError && <ErrorMsg>{editError}</ErrorMsg>}
                <Btn type="button" onClick={() => setEditing(false)} disabled={saving}>
                  <MdClose /> Cancelar
                </Btn>
                <Btn type="submit" primary disabled={saving}>
                  <MdSave /> {saving ? 'Guardando...' : 'Guardar'}
                </Btn>
              </EditButtons>
            )}
          </Card>
        </ContentGrid>
      </form>

      {/* ── Timeline de actividades ── */}
      <SectionCard>
        <CardTitle><MdHistory />Timeline de Actividades</CardTitle>

        <form onSubmit={handleAddActividad}>
          <AddActivityForm>
            <Select
              value={newAct.tipo}
              onChange={e => setNewAct(prev => ({ ...prev, tipo: e.target.value }))}
              style={{ flexShrink: 0, width: 130 }}
            >
              {TIPO_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </Select>
            <TextArea
              value={newAct.descripcion}
              onChange={e => setNewAct(prev => ({ ...prev, descripcion: e.target.value }))}
              placeholder="Descripción de la actividad…"
              style={{ flex: 1, minHeight: 56, resize: 'vertical' }}
            />
            <Btn type="submit" primary disabled={addingAct || !newAct.descripcion.trim()} style={{ alignSelf: 'flex-end' }}>
              <MdAdd /> {addingAct ? 'Guardando…' : 'Agregar'}
            </Btn>
          </AddActivityForm>
        </form>

        {actLoading ? (
          <EmptyMsg>Cargando actividades…</EmptyMsg>
        ) : actividades.length === 0 ? (
          <EmptyMsg>No hay actividades registradas. Sé el primero en agregar una.</EmptyMsg>
        ) : (
          <ActivityList>
            {actividades.map(act => (
              <ActivityItem key={act.id}>
                <ActivityIconWrap tipo={act.tipo}>{actIcon(act.tipo)}</ActivityIconWrap>
                <ActivityBody>
                  <ActivityDesc>{act.descripcion}</ActivityDesc>
                  <ActivityMeta>
                    {act.usuario_nombre && <strong>{act.usuario_nombre} · </strong>}
                    {fmtDatetime(act.created_at)}
                    {' · '}
                    {act.tipo}
                  </ActivityMeta>
                </ActivityBody>
                <ActivityDeleteBtn type="button" onClick={() => handleDeleteActividad(act.id)} title="Eliminar">
                  <MdDelete />
                </ActivityDeleteBtn>
              </ActivityItem>
            ))}
          </ActivityList>
        )}
      </SectionCard>

      {/* ── Historial de estados ── */}
      {historial.length > 0 && (
        <SectionCard>
          <CardTitle><MdMoreHoriz />Historial de Estado</CardTitle>
          <HistorialList>
            {historial.map(h => (
              <HistorialItem key={h.id}>
                <HistorialDot estado={h.estado_nuevo}>
                  {h.estado_nuevo?.[0]?.toUpperCase()}
                </HistorialDot>
                <HistorialText>
                  {h.estado_anterior
                    ? <><strong>{h.estado_anterior}</strong> → <strong>{h.estado_nuevo}</strong></>
                    : <><strong>Creado</strong> como <strong>{h.estado_nuevo}</strong></>}
                  {h.usuario_nombre && <> · {h.usuario_nombre}</>}
                  <> · {fmtDatetime(h.created_at)}</>
                </HistorialText>
              </HistorialItem>
            ))}
          </HistorialList>
        </SectionCard>
      )}

      <Adjuntos entidadTipo="lead" entidadId={id} />
      <CalendarioRecordatorios entidadTipo="lead" entidadId={id} />
    </Container>
  );
};

export default LeadDetail;
